import React, { DependencyList, useCallback, useEffect, useRef, useState } from 'react';

import { OrgChartEmployee } from '../interfaces';

interface UseMasonryLayoutOptions<T> {
  items: T[];
  columnWidth: number;
  containerPadding?: number;
  getItemHeight: (item: T) => number;
  dependencies?: DependencyList;
}

interface MasonryItem<T> {
  item: T;
  column: number;
}

// GENERIC MASONRY LAYOUT HOOK
export function useMasonryLayout<T>({
  items,
  columnWidth,
  containerPadding = 0,
  getItemHeight,
  dependencies = [],
}: UseMasonryLayoutOptions<T>) {
  const [gridColumns, setGridColumns] = useState(3);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateGridColumns = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth - containerPadding;
      const newGridColumns = Math.max(1, Math.floor(containerWidth / columnWidth));
      setGridColumns(newGridColumns);
    }
  }, [containerPadding, columnWidth]);

  useEffect(() => {
    if (!containerRef.current) return;

    calculateGridColumns();

    let rafId: number | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        calculateGridColumns();
      });
    });

    resizeObserver.observe(containerRef.current);

    const handleWindowResize = () => {
      calculateGridColumns();
    };

    window.addEventListener('resize', handleWindowResize);

    // Force a calculation after initial render
    setTimeout(calculateGridColumns, 0);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [calculateGridColumns]);

  useEffect(() => {
    calculateGridColumns();
  }, [dependencies, calculateGridColumns]);

  const createMasonryLayout = useCallback(() => {
    if (items.length === 0) return [];

    const columnHeights = Array(gridColumns).fill(0);
    const layout: MasonryItem<T>[] = [];

    items.forEach((item) => {
      const minHeightColumn = columnHeights.indexOf(Math.min(...columnHeights));

      layout.push({ item, column: minHeightColumn });

      const itemHeight = getItemHeight(item);
      columnHeights[minHeightColumn] += itemHeight;
    });

    return layout;
  }, [items, gridColumns, getItemHeight]);

  const masonryLayout = createMasonryLayout();

  return {
    containerRef,
    gridColumns,
    masonryLayout,
  };
}

const defaultContainerPadding = 96;
const defaultBaseCardHeight = 220;
const defaultReportHeight = 100;

export function useEmployeeMasonryLayout({
  reports,
  expandedEmployees,
  columnWidth,
  containerPadding = defaultContainerPadding,
  baseCardHeight = defaultBaseCardHeight,
  reportHeight = defaultReportHeight,
  dependencies = [],
}: {
  reports: OrgChartEmployee[];
  expandedEmployees: Set<string>;
  columnWidth: number;
  containerPadding?: number;
  baseCardHeight?: number;
  reportHeight?: number;
  dependencies?: DependencyList;
}) {
  const expandedLayouts = React.useMemo(() => {
    const layouts: Record<string, number> = {};

    if (reports.length > 0) {
      reports.forEach((employee) => {
        if (expandedEmployees.has(employee.id)) {
          const employeeReports = employee.reports || [];
          if (employeeReports.length > 0) {
            layouts[employee.id] = employeeReports.length * reportHeight;
          }
        }
      });
    }

    return layouts;
  }, [reports, expandedEmployees, reportHeight]);

  const getEmployeeHeight = useCallback(
    (employee: OrgChartEmployee) => {
      return baseCardHeight + (expandedLayouts[employee.id] || 0);
    },
    [expandedLayouts, baseCardHeight]
  );

  return useMasonryLayout({
    items: reports,
    columnWidth,
    containerPadding,
    getItemHeight: getEmployeeHeight,
    dependencies,
  });
}
